import React, { useCallback, useEffect, useState } from 'react';

/**
 * Catalogue › Appli mobile (01/10/2026, packs le 04/10/2026) : ce que l'appli
 * SEmply peut ouvrir vers le site, un interrupteur par système et par usage :
 *
 *  - le CHANGEMENT DE FORMULE (zone « Formule » de Mon compte) ;
 *  - la GESTION DES PACKS (écran « Packs » de Mon compte).
 *
 * Les deux se règlent à part : on peut ouvrir l'un sans l'autre.
 *
 * ── Ce que fait un interrupteur, et ce qu'il ne fait pas ───────────────────
 *
 * Fermé (défaut) : l'appli montre l'existant (formule en cours, crédits et
 * achats), sans lien, sans prix, avec un message neutre.
 *
 * Ouvert : l'appli affiche un lien vers le site — MAIS seulement si son BUILD
 * le prévoit aussi. C'est la seconde clé, et elle est voulue : Apple refuse
 * une fonction allumée à distance après la revue (règle 2.3.1). Le lien vers
 * un achat hors de l'appli exige en plus, sur iPhone, l'autorisation
 * « StoreKit External Purchase » d'Apple (et sa feuille d'information), et
 * sur Android l'inscription au programme « External offers » de Google, avec
 * la déclaration des ventes et la commission prévues par chacun.
 *
 * Tant que ces autorisations ne sont pas obtenues et intégrées dans un build
 * publié, ouvrir un interrupteur ne change RIEN dans l'appli — c'est sans
 * danger, mais c'est aussi sans effet.
 *
 * Enregistrement explicite (bouton), comme l'écran Ouverture : on ne bascule
 * pas une décision commerciale en cliquant à côté.
 */

const USAGES = [
  {
    cle: 'formule',
    titre: 'Changement de formule dans l’appli',
    champs: { ios: 'mobilePlanChangeIos', android: 'mobilePlanChangeAndroid' },
    explication:
      'Fermé, l’appli affiche la formule en cours, grisée : « La modification de la formule n’est pas disponible dans l’application. » Ouvert, elle propose un lien vers le site.',
  },
  {
    cle: 'packs',
    titre: 'Achat de packs dans l’appli',
    champs: { ios: 'mobilePackPurchaseIos', android: 'mobilePackPurchaseAndroid' },
    explication:
      'Fermé, l’écran « Packs » montre les crédits restants et les achats, sans prix : « La gestion des packs n’est pas disponible dans l’application. » Ouvert, il propose un lien vers le site, où les packs s’achètent.',
  },
];

const SYSTEMES = [
  {
    cle: 'ios',
    libelle: 'iPhone (App Store)',
    condition:
      'Exige l’autorisation « StoreKit External Purchase » d’Apple et sa feuille d’information, intégrées dans un build publié.',
  },
  {
    cle: 'android',
    libelle: 'Android (Google Play)',
    condition:
      'Exige l’inscription au programme « External offers » de Google Play et son écran d’information, intégrés dans un build publié.',
  },
];

/** `{ formule: { ios, android }, packs: { ios, android } }` depuis les réglages. */
function lire(reglages) {
  const etat = {};
  for (const u of USAGES) {
    etat[u.cle] = {
      ios: Boolean(reglages?.[u.champs.ios]),
      android: Boolean(reglages?.[u.champs.android]),
    };
  }
  return etat;
}

export function AdminAppliMobile({ call, canWrite }) {
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const { reglages } = await call('/admin/ouverture');
      const lu = lire(reglages);
      setForm(lu);
      setInitial(lu);
    } catch (e) {
      setError(String(e.message ?? e));
    }
  }, [call]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const corps = {};
      for (const u of USAGES) {
        corps[u.champs.ios] = form[u.cle].ios;
        corps[u.champs.android] = form[u.cle].android;
      }
      await call('/admin/ouverture', { method: 'PATCH', body: JSON.stringify(corps) });
      setNotice(
        'Réglages enregistrés. L’appli les lit à l’ouverture de « Mon compte » et de l’écran « Packs ».',
      );
      await load();
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  if (!form) {
    return (
      <section className="ap-form">
        <h2>Appli mobile</h2>
        {error ? <p className="ap-error">{error}</p> : <p>Chargement…</p>}
      </section>
    );
  }

  const modifie = USAGES.some(
    (u) => form[u.cle].ios !== initial[u.cle].ios || form[u.cle].android !== initial[u.cle].android,
  );

  return (
    <section className="ap-form">
      <p className="ap-muted">
        Ce que l’appli peut ouvrir vers le site, système par système. Un lien n’apparaît que si le
        build de l’appli le prévoit aussi : sans build compatible, ouvrir ici est sans effet.
      </p>

      {USAGES.map((u) => (
        <div key={u.cle} className="ap-section">
          <h2>{u.titre}</h2>
          <p className="ap-muted">{u.explication}</p>
          {SYSTEMES.map((s) => (
            <div key={s.cle}>
              <label className="ap-check">
                <input
                  type="checkbox"
                  checked={form[u.cle][s.cle]}
                  disabled={!canWrite || busy}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, [u.cle]: { ...f[u.cle], [s.cle]: e.target.checked } }))
                  }
                />
                {s.libelle} : {form[u.cle][s.cle] ? 'ouvert' : 'fermé'}
              </label>
              <p className="ap-hint">{s.condition}</p>
            </div>
          ))}
        </div>
      ))}

      {error && <p className="ap-error">{error}</p>}
      {notice && <p className="ap-notice">{notice}</p>}

      {canWrite && (
        <div className="ap-actions">
          <button className="ap-btn primary" onClick={save} disabled={busy || !modifie}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button className="ap-btn" onClick={() => setForm(initial)} disabled={busy || !modifie}>
            Annuler
          </button>
        </div>
      )}
    </section>
  );
}

export default AdminAppliMobile;
